'use client';

/**
 * FormShell — Phase A3 single-page form scaffold.
 *
 * Standard form chrome for every single-page form in the platform:
 * sections, sticky save bar, dirty-state warning, autosave indicator,
 * error summary. Consumer owns the actual field rendering + validation
 * + onSubmit — the shell just wraps everything in a predictable layout.
 * With `header`, PageHeader replaces the legacy header: `headerAside` is
 * ignored, and `autosaveIndicator` takes precedence over `header.meta` when
 * it is not null or undefined. Without `header`, the legacy header renders
 * both `autosaveIndicator` and `headerAside`.
 *
 * Anatomy:
 *   ┌────────────────────────────────────────────────┐
 *   │  Title                          [autosave 5s ago]
 *   │  Subtitle / description                         │
 *   │                                                 │
 *   │  [Error summary, if any field invalid]          │
 *   │                                                 │
 *   │  ┌── Section 1 ─────────────────────────────┐   │
 *   │  │ Title                                    │   │
 *   │  │ Optional description                     │   │
 *   │  │ <children: fields>                       │   │
 *   │  └──────────────────────────────────────────┘   │
 *   │  ┌── Section 2 ─────────────────────────────┐   │
 *   │  │ …                                        │   │
 *   │  └──────────────────────────────────────────┘   │
 *   ├─────────────────────────────────────────────────┤
 *   │ [Cancel] [Save as draft]  ←  →  [Save]          │  ← sticky bottom bar
 *   └─────────────────────────────────────────────────┘
 *
 * Spec: `UX_PRODUCTION_ROADMAP.md` §A3 (FormShell).
 *
 * v0.1 scope (this commit): layout chrome, sticky save bar, dirty-state
 * warning trigger, error summary, autosave indicator. Field-level
 * validation, inline help, autosave wiring itself are the consumer's
 * responsibility — the shell just renders the chrome around them.
 */

import React from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { PageTitle } from '../typography';
import { FormActions } from '../form-actions';
import { Form } from '../form';
import { PageHeader, type PageHeaderProps } from '../page-header';
import { Stepper, type StepperProps } from '../stepper';
import type { FieldDensity } from '../input';

export type FormShellHeader = Pick<
  PageHeaderProps,
  | 'breadcrumbs'
  | 'breadcrumbLinkRender'
  | 'actions'
  | 'status'
  | 'meta'
  | 'about'
  | 'updated'
  | 'aboutInOverflow'
>;

export interface FormShellSection {
  id: string;
  title: string;
  description?: React.ReactNode;
  /** Field nodes. */
  children: React.ReactNode;
  /** Optional sticky right-side annotation (e.g. "5 required fields"). */
  annotation?: React.ReactNode;
}

export interface FormShellErrorSummaryItem {
  /** Field id — used for "Jump to field" anchor links if `fieldHref` set. */
  fieldId: string;
  /** Display label (the field name). */
  label: string;
  /** Validation message. */
  message: string;
}

export interface FormShellProps {
  /** Form title. */
  title: string;
  /** Optional subtitle / description rendered under the title. */
  description?: React.ReactNode;
  /** Sections rendered in order. */
  sections: ReadonlyArray<FormShellSection>;
  /**
   * Validation errors aggregated for the top-of-form summary. When
   * non-empty, a banner lists each error with a "Jump to field" anchor.
   */
  errors?: ReadonlyArray<FormShellErrorSummaryItem>;
  /**
   * Autosave indicator text — e.g. "Saved 5s ago", "Saving…", "Draft".
   * Renders top-right of the header when present.
   */
  autosaveIndicator?: React.ReactNode;
  /** Right-side header slot (e.g. status pills, owner avatar). */
  headerAside?: React.ReactNode;
  /** Use PageHeader with breadcrumbs and structured context. Requires a string description. */
  header?: FormShellHeader;
  /** Journey progress between the error summary and the form sections. */
  stepper?: StepperProps;
  /** Docked actions at the form root, outside every section. */
  actions?: React.ReactNode;
  /**
   * Bottom sticky bar actions. Consumer controls the layout — typically
   * a left group (Cancel / Save as draft) and a right group (Save).
   */
  unsaved?: boolean;
  saving?: boolean;
  stickyBarLeft?: React.ReactNode;
  stickyBarRight?: React.ReactNode;
  /**
   * Form `onSubmit` handler. Wired to the outer canonical form root so pressing
   * Enter on a field submits, and the save button can be type="submit".
   */
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  className?: string;
  density?: FieldDensity;
}

export function FormShell({
  title,
  description,
  sections,
  errors,
  autosaveIndicator,
  headerAside,
  header,
  stepper,
  actions,
  unsaved,
  saving = false,
  stickyBarLeft,
  stickyBarRight,
  onSubmit,
  className,
  density = 'productive',
}: FormShellProps) {
  const surface =
    density === 'comfort' ? 'rounded-card-comfort p-7 shadow-card-comfort' : 'rounded-card p-5';
  const hasErrors = errors && errors.length > 0;
  const hasStickyBar = Boolean(stickyBarLeft || stickyBarRight);
  if (process.env.NODE_ENV !== 'production' && actions && hasStickyBar) {
    console.warn('FormShell actions replace stickyBarLeft and stickyBarRight.');
  }
  if (
    process.env.NODE_ENV !== 'production' &&
    header &&
    description != null &&
    typeof description !== 'string'
  ) {
    console.warn('FormShell header requires a string description.');
  }
  return (
    <Form
      onSubmit={onSubmit}
      className={cn('flex min-w-0 flex-col gap-4', className)}
      noValidate
      data-form-shell
    >
      {header ? (
        <PageHeader
          {...header}
          title={title}
          description={typeof description === 'string' ? description : undefined}
          meta={autosaveIndicator ?? header.meta}
        />
      ) : (
        <header className={cn('flex min-w-0 flex-wrap items-start gap-x-4 gap-y-3')}>
          <div className="min-w-0 flex-[1_1_16rem]">
            <PageTitle className="experience-candidate-heading break-normal [overflow-wrap:break-word]">
              {title}
            </PageTitle>
            {description && (
              <p className="experience-candidate-body mt-1 break-words text-body text-text-muted">
                {description}
              </p>
            )}
          </div>
          <div className="ml-auto flex min-w-0 max-w-full flex-[0_1_auto] flex-wrap items-center justify-start gap-2">
            {autosaveIndicator && (
              <span className="experience-candidate-muted min-w-0 break-words font-mono text-caption text-text-muted">
                {autosaveIndicator}
              </span>
            )}
            {headerAside}
          </div>
        </header>
      )}

      {hasErrors && (
        <div
          role="alert"
          aria-live="polite"
          className="experience-candidate-status rounded-card border border-status-danger-border bg-status-danger-bg p-4"
          data-experience-tone="danger"
        >
          <div className="flex items-center gap-2">
            <AlertCircle
              className="experience-candidate-status-glyph h-4 w-4 text-status-danger-fg"
              aria-hidden="true"
            />
            <p className="experience-candidate-status-copy text-sm font-semibold text-status-danger-fg">
              {errors!.length} {errors!.length === 1 ? 'field needs' : 'fields need'} attention
            </p>
          </div>
          <ul
            role="list"
            className="experience-candidate-status-copy mt-2 space-y-1 pl-6 text-caption text-status-danger-fg"
          >
            {errors!.map((err) => (
              <li key={err.fieldId} className="list-disc">
                <a
                  href={`#${err.fieldId}`}
                  className="experience-candidate-focus underline hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                >
                  <span className="font-semibold">{err.label}:</span> {err.message}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {stepper && <Stepper {...stepper} />}

      {sections.map((section) => (
        <section
          key={section.id}
          id={`section-${section.id}`}
          aria-labelledby={`section-${section.id}-heading`}
          className={cn(
            'experience-candidate-surface-base min-w-0 border border-border bg-surface-sheet',
            surface,
          )}
        >
          <div className="mb-4 flex min-w-0 flex-wrap items-start gap-x-4 gap-y-3">
            <div className="min-w-0 flex-[1_1_16rem]">
              <h2
                id={`section-${section.id}-heading`}
                className="experience-candidate-heading break-normal [overflow-wrap:break-word] font-body text-section-title font-semibold text-text-heading"
              >
                {section.title}
              </h2>
              {section.description && (
                <p className="experience-candidate-body mt-1 break-words text-body text-text-muted">
                  {section.description}
                </p>
              )}
            </div>
            {section.annotation && (
              <div className="ml-auto flex min-w-0 max-w-full flex-[0_1_auto] flex-wrap items-center justify-start gap-2 experience-candidate-muted break-words text-caption text-text-muted">
                {section.annotation}
              </div>
            )}
          </div>
          <div className="min-w-0 space-y-3">{section.children}</div>
        </section>
      ))}

      {actions ??
        (hasStickyBar && (
          <FormActions
            role="region"
            unsaved={unsaved}
            saving={saving}
            primary={stickyBarRight}
            secondary={stickyBarLeft}
          />
        ))}
    </Form>
  );
}
